/**
 * Visão por canal (antiga tela inicial; a "/" agora é o painel geral, General.tsx): números rápidos (SKUs, anúncios, faltam anunciar, ruptura com planilha), projeção de vendas do dia,
 * da semana e do mês (média de dias úteis e fins de semana), buy box perdido, itens que pararam de vender
 * (semana × semana e mês × mês) e os 50 mais vendidos. Filtro de canal no topo (padrão: todos).
 */
import { Link } from 'react-router';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Download, Swords } from 'lucide-react';
import type { CatalogSummary, MatchStatus } from '@berytus/contracts';
import { can } from '@berytus/contracts';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, MATCH } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { useUrlState } from './MeliChannel.tsx';
import { ChannelBoard } from './ChannelBoard.tsx';

export type Channel = '' | 'mercadolivre' | 'shopee' | 'ifood';
type Period = 'day' | 'week' | 'month';
interface Stopped { count: number; lostAmount: number; items: { id: string; sku: string; title: string; prevUnits: number; prevAmount: number; lastSale: string | null; available: number }[] }
interface Home {
  cards: { skus: number; activeListings: number; listed: number; missing: number; rupture: { skus: number; listings: number } };
  buybox: { losing: number; byPrice: number; total: number } | null;
  stopped: { week: Stopped; month: Stopped };
  top: Top;
}
interface Projection {
  period: Period; from: string; to: string;
  realized: { amount: number; orders: number };
  projected: { amount: number; orders: number } | null;
  previous: { from: string; to: string; amount: number; orders: number };
  growthPct: number | null;
  average: Record<'business' | 'weekend', { amount: number; orders: number; days: number } | null>;
}

const CHANNELS: [Channel, string][] = [['', 'Todos os canais'], ['mercadolivre', 'Mercado Livre'], ['shopee', 'Shopee'], ['ifood', 'iFood']];
const PERIODS: [Period, string][] = [['day', 'Dia'], ['week', 'Semana'], ['month', 'Mês']];
const PERIOD_LABEL: Record<Period, { now: string; prev: string }> = {
  day: { now: 'hoje', prev: 'o mesmo dia da semana passada' },
  week: { now: 'nesta semana', prev: 'a semana passada' },
  month: { now: 'neste mês', prev: 'o mês passado' },
};
const ORDER: MatchStatus[] = ['matched', 'manual', 'ambiguous', 'unmatched', 'no_gtin', 'pending', 'kit'];
const short = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

export function Overview() {
  const me = useMe();
  const [params, set] = useUrlState();
  const channel = (params.get('canal') ?? '') as Channel;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>{me.tenant.name}</h1>
          <p>Números do catálogo e dos anúncios, projeção de vendas, buy box, o que parou de vender e os mais vendidos.</p>
        </div>
      </div>
      <div className="stack-y">
        <div className="seg" role="group" aria-label="Canal" style={{ justifySelf: 'start', maxWidth: '100%', overflowX: 'auto' }}>
          {CHANNELS.map(([v, l]) => <button key={v} type="button" aria-pressed={channel === v} onClick={() => set({ canal: v || null })}>{l}</button>)}
        </div>
        <HomeBoard channel={channel} />
        <CatalogHealth />
      </div>
    </>
  );
}

/**
 * Os blocos da tela inicial para um canal (ou todos): cards, projeção, buy box, parados e top 50.
 * Também é o "Resumo do canal" do Mercado Livre, da Shopee e do iFood (com o canal fixo).
 */
export function HomeBoard({ channel }: { channel: Channel }) {
  const me = useMe();
  const [params, set] = useUrlState();
  // No painel do canal, a projeção do dia fica no topo (ChannelBoard, ritmo vs. referência); aqui, semana e mês.
  const period = (params.get('periodo') ?? (channel ? 'week' : 'day')) as Period;
  const topDays = params.get('top') === '7' ? 7 : 30;
  const scope = channel ? `channel=${channel}` : '';
  const home = useQuery({
    queryKey: ['home', channel, topDays],
    queryFn: () => api<Home>(`/home?topDays=${topDays}${scope ? `&${scope}` : ''}`),
    placeholderData: keepPreviousData,
    refetchInterval: 120_000,
  });
  const proj = useQuery({
    queryKey: ['home', 'projection', channel, period],
    queryFn: () => api<Projection>(`/home/projection?period=${period}${scope ? `&${scope}` : ''}`),
    placeholderData: keepPreviousData,
    refetchInterval: 120_000,
  });
  const base = channel === 'mercadolivre' ? '/canal/mercadolivre' : channel === 'shopee' ? '/canal/shopee' : null;
  const qs = (extra: string) => `${extra}${scope ? `${extra ? '&' : ''}${scope}` : ''}`;
  const d = home.data;

  return (
    <>
        {channel && <ChannelBoard channel={channel} />}

        {home.error ? <ErrorBox error={home.error} /> : !d ? <Loading /> : (
          <div className="kpis" style={{ opacity: home.isPlaceholderData ? 0.55 : 1 }}>
            <Link className="kpi" to="/catalogo">
              <span className="k-label">SKUs</span><span className="k-value">{int(d.cards.skus)}</span><span className="k-note">produtos ativos na Berytus</span>
            </Link>
            <Link className="kpi" to={base ? `${base}/anuncios?status=active` : '/canais'}>
              <span className="k-label">Anúncios ativos</span><span className="k-value">{int(d.cards.activeListings)}</span>
              <span className="k-note">{int(d.cards.listed)} SKUs com anúncio ativo ou pausado</span>
            </Link>
            <Link className={`kpi${d.cards.missing > 0 ? ' alert' : ''}`}
              to={base === '/canal/shopee' ? '/canal/shopee/publicar' : base ? `${base}/produtos?has=without` : '/catalogo'}>
              <span className="k-label">Faltam anunciar</span><span className="k-value">{int(d.cards.missing)}</span>
              <span className="k-note">SKUs sem anúncio {channel ? `no ${CHANNELS.find(([v]) => v === channel)![1]}` : 'em nenhum canal'}</span>
            </Link>
            <a className={`kpi${d.cards.rupture.skus > 0 ? ' alert' : ''}`} href={`/api/v1/home/rupture.csv${qs('') ? `?${qs('')}` : ''}`}
              title="Baixar a planilha: SKU, nome e quanto vende por dia">
              <span className="k-label">Em ruptura <Download size={13} style={{ verticalAlign: -2 }} /></span>
              <span style={{ display: 'flex', gap: 18, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span><span className="k-value" style={{ display: 'inline' }}>{int(d.cards.rupture.skus)}</span> <span className="small">SKUs</span></span>
                <span><span className="k-value" style={{ display: 'inline' }}>{int(d.cards.rupture.listings)}</span> <span className="small">anúncio{d.cards.rupture.listings === 1 ? '' : 's'}</span></span>
              </span>
              <span className="k-note">anunciados e sem estoque · clique para a planilha</span>
            </a>
          </div>
        )}

        <ProjectionPanel q={proj} period={period} periods={channel ? PERIODS.filter(([v]) => v !== 'day') : PERIODS}
          onPeriod={(p) => set({ periodo: p === (channel ? 'week' : 'day') ? null : p })} />

        {d?.buybox && <BuyBoxNotice bb={d.buybox} />}

        {d && (
          <div className="cols">
            <StoppedPanel title="Pararam de vender: semana × semana" note="vendeu nos 7 dias anteriores e nada nos últimos 7" s={d.stopped.week}
              csv={`/api/v1/home/stopped.csv?${qs('period=week')}`} />
            <StoppedPanel title="Pararam de vender: mês × mês" note="vendeu nos 30 dias anteriores e nada nos últimos 30" s={d.stopped.month}
              csv={`/api/v1/home/stopped.csv?${qs('period=month')}`} />
          </div>
        )}

        {d && <TopPanel top={d.top} onDays={(v) => set({ top: v === 30 ? null : String(v) })} csv={`/api/v1/home/top.csv?${qs(`topDays=${topDays}`)}`} />}
    </>
  );
}

export interface Top { days: number; items: { id: string; sku: string; title: string; units: number; orders: number; amount: number; perDay: number; available: number; listings: number }[] }

/** Top 50 mais vendidos (30 ou 7 dias), com Exportar. Usado na visão por canal e na tela inicial do canal. */
export function TopPanel({ top, onDays, csv, faded }: { top: Top; onDays: (days: 7 | 30) => void; csv: string; faded?: boolean }) {
  return (
          <section className="panel flush" style={{ opacity: faded ? 0.55 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '14px 16px' }}>
              <h2 style={{ margin: 0 }}>Top 50 mais vendidos</h2>
              <div className="seg" role="group" aria-label="Período do top 50">
                {([30, 7] as const).map((v) => <button key={v} type="button" aria-pressed={top.days === v} onClick={() => onDays(v)}>{v} dias</button>)}
              </div>
              <span style={{ flex: 1 }} />
              <a className="btn sm" href={csv}><Download size={14} /> Exportar</a>
            </div>
            {top.items.length === 0 ? <div className="empty"><b>Sem vendas no período</b>Nenhum pedido pago nos últimos {top.days} dias.</div> : (
              <div className="grid-wrap">
                <table className="grid" style={{ width: '100%', minWidth: 760 }}>
                  <colgroup>{[52, 120, 0, 96, 84, 120, 92, 96].map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                  <thead>
                    <tr><th className="num">#</th><th>SKU</th><th>Produto</th><th className="num">Unidades</th><th className="num">Por dia</th><th className="num">Vendido</th><th className="num">Estoque</th><th className="num">Anúncios</th></tr>
                  </thead>
                  <tbody>
                    {top.items.map((r, i) => (
                      <tr key={r.id} style={{ cursor: 'default' }}>
                        <td className="num muted">{i + 1}</td>
                        <td><Link className="code" to={`/catalogo/${r.id}`}>{r.sku}</Link></td>
                        <td style={{ whiteSpace: 'normal' }}>{r.title}</td>
                        <td className="num">{int(r.units)}</td>
                        <td className="num">{r.perDay.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
                        <td className="num">{brl(r.amount)}</td>
                        <td className="num" style={r.available <= 0 ? { color: 'var(--err)', fontWeight: 600 } : undefined}>{int(r.available)}</td>
                        <td className="num">{int(r.listings)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="cards">
              {top.items.map((r, i) => (
                <Link key={r.id} to={`/catalogo/${r.id}`} className="card-row" style={{ gridTemplateColumns: '1fr auto' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="t">{i + 1}. {r.title}</div>
                    <div className="m">SKU {r.sku} · {int(r.units)} un. · {r.perDay.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}/dia</div>
                  </div>
                  <div className="r">
                    <div>{brl(r.amount)}</div>
                    <div className={r.available <= 0 ? undefined : 'muted'} style={r.available <= 0 ? { color: 'var(--err)' } : undefined}>{int(r.available)} em estoque</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
  );
}

/** Aviso do buy box do Mercado Livre (anúncios de catálogo ativos em que outro vendedor ganha). */
export function BuyBoxNotice({ bb }: { bb: { losing: number; byPrice: number; total: number } }) {
  const me = useMe();
  return (
          <div className={`notice${bb.losing > 0 ? ' warn' : ''}`} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Swords size={16} />
            {bb.losing > 0 ? (
              <>
                <span>
                  <b>{int(bb.losing)}</b> ite{bb.losing === 1 ? 'm' : 'ns'} perdendo o buy box no Mercado Livre
                  {bb.byPrice > 0 && <> ({int(bb.byPrice)} só por preço)</>}.
                </span>
                {can(me.tenant.access, 'buybox')
                  ? <Link to="/canal/mercadolivre/concorrentes?bb=competing">Clique aqui para verificar</Link>
                  : <span className="muted small">Fale com o administrador para revisar.</span>}
              </>
            ) : <span>Nenhum item perdendo o buy box no Mercado Livre ({int(bb.total)} anúncios de catálogo).</span>}
          </div>
  );
}

function ProjectionPanel({ q, period, periods, onPeriod }: { q: UseQueryResult<Projection>; period: Period; periods: [Period, string][]; onPeriod: (p: Period) => void }) {
  const p = q.data;
  const label = PERIOD_LABEL[period];
  return (
    <section className="panel stack-y" style={{ gap: 10, opacity: q.isPlaceholderData ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Projeção de vendas</h2>
        <div className="seg" role="group" aria-label="Período da projeção">
          {periods.map(([v, l]) => <button key={v} type="button" aria-pressed={period === v} onClick={() => onPeriod(v)}>{l}</button>)}
        </div>
        {p && <span className="small muted">{p.from === p.to ? short(p.from) : `${short(p.from)} a ${short(p.to)}`}</span>}
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !p ? <Loading /> : !p.projected ? (
        <div className="small"><b>Sem base de comparação.</b> <span className="muted">Ainda não há vendas neste canal para calcular a média de dias úteis e fins de semana.</span></div>
      ) : (
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div className="small muted">Projeção {label.now}</div>
            <div className="tnum" style={{ fontSize: 30, fontWeight: 700 }}>{brl(p.projected.amount)}</div>
            <div className="small muted">~{int(p.projected.orders)} pedidos</div>
          </div>
          <div>
            <div className="small muted">Vendido até agora</div>
            <div className="tnum" style={{ fontSize: 20, fontWeight: 600 }}>{brl(p.realized.amount)}</div>
            <div className="small muted">{int(p.realized.orders)} pedidos</div>
          </div>
          <div>
            <div className="small muted">Comparado com {label.prev}</div>
            {p.growthPct == null ? <div className="small"><b>Sem base de comparação</b></div> : (
              <div className="tnum" style={{ fontSize: 20, fontWeight: 600, color: p.growthPct >= 0 ? 'var(--ok)' : 'var(--err)' }}>
                {p.growthPct >= 0 ? '+' : ''}{p.growthPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
              </div>
            )}
            <div className="small muted">{brl(p.previous.amount)} em {p.previous.from === p.previous.to ? short(p.previous.from) : `${short(p.previous.from)} a ${short(p.previous.to)}`}</div>
          </div>
          <div className="small muted" style={{ marginLeft: 'auto', maxWidth: 360 }}>
            Média das últimas 4 semanas: dia útil {p.average.business ? brl(p.average.business.amount) : 'sem venda'} · fim de semana{' '}
            {p.average.weekend ? brl(p.average.weekend.amount) : 'sem venda'}. Hoje conta o vendido até agora mais o que costuma vender no resto do dia.
          </div>
        </div>
      )}
    </section>
  );
}

function StoppedPanel({ title, note, s, csv }: { title: string; note: string; s: Stopped; csv: string }) {
  return (
    <section className="panel stack-y" style={{ gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0, flex: 1 }}>{title}</h2>
        <a className="btn sm" href={csv} aria-disabled={!s.count}><Download size={14} /> Exportar</a>
      </div>
      <div className="small">
        <b className={s.count ? 'tnum' : undefined} style={s.count ? { color: 'var(--err)' } : undefined}>{int(s.count)}</b> ite{s.count === 1 ? 'm' : 'ns'}
        {s.count > 0 && <> · vendiam {brl(s.lostAmount)} no período anterior</>} <span className="muted">({note})</span>
      </div>
      {s.items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
        <table className="grid" style={{ width: '100%', minWidth: 460 }}>
          <colgroup><col /><col style={{ width: 76 }} /><col style={{ width: 92 }} /><col style={{ width: 118 }} /></colgroup>
          <thead><tr><th>Produto</th><th className="num">Antes</th><th className="num">Estoque</th><th>Últ. venda</th></tr></thead>
          <tbody>
            {s.items.map((r) => (
              <tr key={r.id} style={{ cursor: 'default' }}>
                <td style={{ whiteSpace: 'normal' }}>
                  <div className="cell-2"><Link to={`/catalogo/${r.id}`}>{r.title}</Link><span className="sub">{r.sku}</span></div>
                </td>
                <td className="num" title={brl(r.prevAmount)}>{int(r.prevUnits)} un.</td>
                <td className="num" style={r.available <= 0 ? { color: 'var(--err)', fontWeight: 600 } : undefined} title={r.available <= 0 ? 'Sem estoque: provável motivo' : undefined}>{int(r.available)}</td>
                <td className="small">{r.lastSale ? dateTime(r.lastSale).slice(0, 10) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {s.count > s.items.length && <span className="small muted">Mostrando {s.items.length} de {int(s.count)}. A planilha traz todos.</span>}
    </section>
  );
}

/** Vínculo com o catálogo mestre e origem dos dados (o que a tela inicial mostrava antes, mais abaixo). */
function CatalogHealth() {
  const q = useQuery({ queryKey: ['summary'], queryFn: () => api<CatalogSummary>('/catalog/summary') });
  if (q.isPending) return null;
  if (q.error) return <ErrorBox error={q.error} />;
  const s = q.data;
  const activeTotal = ORDER.reduce((n, k) => n + s.byStatus[k], 0) || 1;
  const shown = ORDER.filter((k) => s.byStatus[k] > 0);
  return (
    <div className="cols">
      <section className="panel">
        <h2>Vínculo com o catálogo mestre</h2>
        <div className="meter" role="img" aria-label="Distribuição dos produtos ativos por status de vínculo">
          {shown.map((k) => <div key={k} style={{ width: `${(100 * s.byStatus[k]) / activeTotal}%`, background: MATCH[k].color }} />)}
        </div>
        {shown.map((k) => (
          <div className="row-line" key={k}>
            <Link to={`/catalogo?status=${k}`}><span className="dot" style={{ background: MATCH[k].color }} />{MATCH[k].label}</Link>
            <span className="tnum">{int(s.byStatus[k])}</span>
          </div>
        ))}
      </section>
      <section className="panel">
        <h2>Origem dos dados</h2>
        <div className="row-line"><span>Última importação concluída</span><span className="tnum">{dateTime(s.lastImportAt)}</span></div>
        <div className="row-line"><span>Com estoque disponível</span><span className="tnum">{int(s.withStock)}</span></div>
        <div className="row-line"><span>Códigos de barras com problema</span><span className="tnum">{int(s.invalidGtins)}</span></div>
        <p className="small muted" style={{ margin: '10px 0 0' }}>
          Códigos com problema não impedem o vínculo quando outro EAN do mesmo produto é válido.{' '}
          <Link to="/importacoes">Ver importações</Link>
        </p>
      </section>
    </div>
  );
}
